import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { listArtists, type ArtistCard, type ArtistFilters } from '../../lib/artists';
import { ApiError } from '../../lib/apiClient';
import { ReliabilityBadge } from '../../components/ReliabilityBadge';
import './ArtistsPage.css';

/**
 * The public artist directory.
 *
 * Filters live in the URL rather than component state, matching EventsPage: the engine
 * filters server side on genre, market and tier, so a filtered view stays shareable and
 * survives the back button.
 *
 * Every card shows reliability through the shared badge, so a new artist reads as
 * "building track record" and never as a zero. That distinction is the reason the badge
 * exists, and inventing a second treatment here would undo it.
 */
function readFilters(params: URLSearchParams): ArtistFilters {
  return {
    genre: params.get('genre') ?? undefined,
    market: params.get('market') ?? undefined,
    tier: params.get('tier') ?? undefined,
  };
}

function Card({ artist }: { artist: ArtistCard }) {
  const meta = [artist.genre, artist.primaryMarket].filter(Boolean).join(' | ');

  return (
    <Link to={`/artists/${artist.artistId}`} className="artist-card glass-card glass-card--grid">
      <div className="artist-card__media">
        {artist.profileImageUrl ? (
          <img src={artist.profileImageUrl} alt="" loading="lazy" />
        ) : (
          <div className="artist-card__media-fallback" aria-hidden="true">
            <span className="display">{artist.stageName.slice(0, 1)}</span>
          </div>
        )}
      </div>

      <div className="artist-card__body">
        <h3 className="artist-card__name">{artist.stageName}</h3>
        {meta && <p className="artist-card__meta mono">{meta}</p>}

        {artist.bioSnippet && (
          <p className="artist-card__bio">
            {artist.bioSnippet}
            {artist.bioTruncated ? '…' : ''}
          </p>
        )}

        <div className="artist-card__footer">
          <ReliabilityBadge
            score={artist.reliabilityScore}
            buildingTrackRecord={artist.buildingTrackRecord}
          />
          {artist.followers > 0 && (
            <span className="artist-card__followers mono">
              {artist.followers.toLocaleString()} following
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export function ArtistsPage() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readFilters(params), [params]);

  const [artists, setArtists] = useState<ArtistCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listArtists(filters)
      .then((rows) => {
        if (cancelled) return;
        setArtists(rows);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load artists.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [filters]);

  const setFilter = useCallback((key: keyof ArtistFilters, value: string) => {
    const next = new URLSearchParams(params);
    if (value.trim() === '') next.delete(key);
    else next.set(key, value.trim());
    setParams(next, { replace: true });
  }, [params, setParams]);

  // Derived from what actually came back, so the pickers never offer a genre or a market
  // that would return an empty list.
  const genres = useMemo(
    () => [...new Set(artists.map((a) => a.genre).filter(Boolean))].sort() as string[],
    [artists],
  );
  const markets = useMemo(
    () => [...new Set(artists.map((a) => a.primaryMarket).filter(Boolean))].sort() as string[],
    [artists],
  );

  const hasFilters = Boolean(filters.genre || filters.market || filters.tier);

  return (
    <div className="artists-page">
      <header className="artists-hero">
        <h1 className="artists-hero__title display">Artists on Voleto</h1>
        <p className="artists-hero__lede">
          Performers with a booking history on the platform. Reliability is built from
          completed events, not self-reported.
        </p>
      </header>

      <div className="artists-filters">
        <div className="field">
          <label className="field__label" htmlFor="artist-genre">Genre</label>
          <select
            id="artist-genre"
            className="field__input"
            value={filters.genre ?? ''}
            onChange={(e) => setFilter('genre', e.target.value)}
          >
            <option value="">All genres</option>
            {genres.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="artist-market">Market</label>
          <select
            id="artist-market"
            className="field__input"
            value={filters.market ?? ''}
            onChange={(e) => setFilter('market', e.target.value)}
          >
            <option value="">All markets</option>
            {markets.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {hasFilters && (
          <button
            type="button"
            className="button button--ghost"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
          >
            Clear filters
          </button>
        )}
      </div>

      {loading && <p className="artists-state">Loading artists…</p>}

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {!loading && !error && artists.length === 0 && (
        <div className="artists-empty">
          <h2>No artists to show</h2>
          <p>
            {hasFilters
              ? 'Nothing matches those filters yet. Try clearing them.'
              : 'Artists appear here once their profile is approved.'}
          </p>
        </div>
      )}

      {artists.length > 0 && (
        <div className="artists-grid">
          {artists.map((artist) => (
            <Card key={artist.artistId} artist={artist} />
          ))}
        </div>
      )}
    </div>
  );
}
